import type { ParseKeys } from 'i18next';
import { TimeRange } from '@/services/dashboard-service/dashboard.service.types';

/**
 * Widget areas this screen no longer mounts; the code stays in the repo. Carries the
 * dictionary key of the section title each renders (`metrics` namespace), so a test can
 * iterate the list and resolve the title in the language of the UI.
 *
 * The company picker is absent deliberately: it selects the tenant rather than
 * showing a metric, and the access guard can force it to change.
 */
export const HIDDEN_DASHBOARD_WIDGETS = [
  // `CustomSwitch` has no title; it goes with the SMS branch it toggled.
  { key: 'sms-metric', titleKey: 'sections.smsMetrics' },
  { key: 'call-metric', titleKey: 'sections.callMetrics' },
  { key: 'insights', titleKey: 'sections.insights' },
  { key: 'ai-performance', titleKey: 'sections.aiPerformance' },
] as const satisfies readonly { key: string; titleKey: ParseKeys<'metrics'> }[];

/** The mockup's stated assumption behind the saved-time tile. */
export const MINUTES_SAVED_PER_CASE = 8;

/** Rows in the recent-calls snapshot. */
export const RECENT_CALL_ROWS = 5;

export type RangeKey = TimeRange | 'custom';

// Keyed on the preset union, so adding a preset fails to compile rather than
// silently falling through to the custom-range wording.
const RANGE_KEYS: Record<RangeKey, true> = {
  '24h': true,
  '3d': true,
  '7d': true,
  '30d': true,
  '1y': true,
  custom: true,
};

/**
 * Names the span, not the preset: bucket count follows the calendar span. Returns the key
 * the range wording is filed under (`dashboard:kpis.calls.range.*`, `metrics:range.in.*`,
 * `statistics:deltaCaption.*`), since Polish needs a different case of the same phrase in
 * each sentence and one label cannot serve them all.
 */
export function rangeKey(preset: string | null): RangeKey {
  return preset !== null && Object.keys(RANGE_KEYS).includes(preset)
    ? (preset as RangeKey)
    : 'custom';
}
